import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface AffinityItem {
  platform: string;
  minutes: number;
  percentage: number;
}

function AffinityBars({ items }: { items: AffinityItem[] }) {
  if (!items.length) return <LockedContent message={message} />;

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.platform} className="flex items-center gap-2">
          <p className="w-16 shrink-0 text-sm text-foreground truncate">{item.platform}</p>
          <div className="min-w-0 flex-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full" style={{ width: `${item.percentage}%`, background: "linear-gradient(to right, #93c5fd, #2563eb)" }} />
            </div>
          </div>
          <p className="shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">{Math.round(item.minutes)} min/day</p>
        </div>
      ))}
    </div>
  );
}

function AffinityContent({ status, items }: { status: QueryStatus; items: AffinityItem[] }) {
  switch (status) {
    case "pending":
      return <AffinitySkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return <AffinityBars items={items} />;
  }
}

export function PlatformAffinity() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchTargetAudience());

  const raw = data?.platform_affinity ?? [];
  const maxMinutes = Math.max(...raw.map((i) => i.absolute_minutes_spent), 0);

  const items = raw
    .filter((item) => item.absolute_minutes_spent > 0 && item.platform)
    .map((item) => ({
      platform: item.platform,
      minutes: item.absolute_minutes_spent,
      percentage: item.relative_percentage != null
        ? Math.min(100, Math.max(0, item.relative_percentage))
        : maxMinutes > 0 ? Math.min(100, (item.absolute_minutes_spent / maxMinutes) * 100) : 0,
    }));

  return (
    <Card className="relative overflow-hidden flex flex-col h-[280px]">
      <CardHeader className="shrink-0">
        <CardTitle className="text-base font-semibold">Platform Affinity</CardTitle>
      </CardHeader>
      <CardContent className="relative pt-0 flex-1 flex flex-col overflow-y-auto scrollbar-hidden">
        <AffinityContent status={status} items={items} />
      </CardContent>
    </Card>
  );
}

function AffinitySkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-2 flex-1 rounded-full" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}
